/**
 * Super Admin Dashboard Entry Point
 * ==================================
 * نقطة الدخول للوحة تحكم المدير العام
 */

import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from '../lib/context/ThemeContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ToastProvider } from './context/ToastContext';
import { DialogProvider } from '../context/DialogContext';

// Layouts
import AdminLayout from './layouts/AdminLayout';
import AuthLayout from './layouts/AuthLayout';

// Pages
import LoginPage from './pages/auth/LoginPage';
import DashboardPage from './pages/dashboard/DashboardPage';
import TenantsPage from './pages/tenants/TenantsPage';
import TenantDetailPage from './pages/tenants/TenantDetailPage';
import UsersPage from './pages/users/UsersPage';
import UserDetailPage from './pages/users/UserDetailPage';
import RolesPage from './pages/roles/RolesPage';
import ReportsPage from './pages/reports/ReportsPage';
import SettingsPage from './pages/settings/SettingsPage';
import RegistrationRequestsPage from './pages/registrations/RegistrationRequestsPage';
import RatingsPage from './pages/ratings/RatingsPage';
import TicketsPage from './pages/tickets/TicketsPage';
import ChatPage from './pages/chat/ChatPage';
import PlatformCouponsPage from './pages/coupons/PlatformCouponsPage';
import ShipsReviewPage from './pages/ships/ShipsReviewPage';
import ShipDetailPage from './pages/ships/ShipDetailPage';
import RequirementsPage from './pages/requirements/RequirementsPage';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="watrly-admin-loading">
        <div className="watrly-admin-loading__spinner" />
        <span>جاري التحميل...</span>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/admin/login" replace />;
  }

  return children;
};

// Admin App Component
const AdminApp = () => {
  return (
    <ThemeProvider initialLocale="ar">
      <AuthProvider>
        <ToastProvider>
          <DialogProvider>
          <BrowserRouter>
            <Routes>
              {/* Auth Routes */}
              <Route element={<AuthLayout />}>
                <Route path="/admin/login" element={<LoginPage />} />
              </Route>

              {/* Protected Admin Routes */}
              <Route
                element={
                  <ProtectedRoute>
                    <AdminLayout />
                  </ProtectedRoute>
                }
              >
                <Route path="/admin" element={<Navigate to="/admin/dashboard" replace />} />
                <Route path="/admin/dashboard" element={<DashboardPage />} />
                <Route path="/admin/tenants" element={<TenantsPage />} />
                <Route path="/admin/tenants/:id" element={<TenantDetailPage />} />
                <Route path="/admin/users" element={<UsersPage />} />
                <Route path="/admin/users/:id" element={<UserDetailPage />} />
                <Route path="/admin/roles" element={<RolesPage />} />
                <Route path="/admin/registrations" element={<RegistrationRequestsPage />} />
                <Route path="/admin/ratings" element={<RatingsPage />} />
                <Route path="/admin/tickets" element={<TicketsPage />} />
                <Route path="/admin/chat" element={<ChatPage />} />
                <Route path="/admin/reports" element={<ReportsPage />} />
                <Route path="/admin/settings" element={<SettingsPage />} />
                <Route path="/admin/platform-coupons" element={<PlatformCouponsPage />} />
                <Route path="/admin/ships-review" element={<ShipsReviewPage />} />
                <Route path="/admin/ships/:tenantId/:shipId" element={<ShipDetailPage />} />
                <Route path="/admin/requirements" element={<RequirementsPage />} />
              </Route>

              {/* Redirect root to admin */}
              <Route path="/" element={<Navigate to="/admin" replace />} />
            </Routes>
          </BrowserRouter>
          </DialogProvider>
        </ToastProvider>
      </AuthProvider>
    </ThemeProvider>
  );
};

export default AdminApp;
